import { useState } from 'react'
import { Table, Button, Modal, Form, Input, DatePicker, Tag, message } from 'antd'
import {  EditOutlined, CalendarOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { hackathonApi, type Hackathon, type UpdateHackathonRequest } from '../services/hackathon'
import ImageUploader from '../components/common/ImageUploader'

const Hackathon = () => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingHackathon, setEditingHackathon] = useState<Hackathon | null>(null)
  const [form] = Form.useForm()
  const [imageGallery, setImageGallery] = useState<{ id: string; value: string }[]>([])
  const [isUploadingGallery, setIsUploadingGallery] = useState(false)
  const queryClient = useQueryClient()

  // Fetch hackathons using React Query
  const { data: hackathonsData, isLoading, error } = useQuery({
    queryKey: ['hackathons'],
    queryFn: hackathonApi.getHackathons,
  })

  // Create hackathon mutation
  const createMutation = useMutation({
    mutationFn: hackathonApi.createHackathon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hackathons'] })
      message.success('Hackathon created successfully!')
      setIsModalVisible(false)
      setImageGallery([])
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create hackathon')
    },
  })

  // Update hackathon mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHackathonRequest }) =>
      hackathonApi.updateHackathon(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hackathons'] })
      message.success('Hackathon updated successfully!')
      setIsModalVisible(false)
      setImageGallery([])
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update hackathon')
    },
  })

  // Delete hackathon mutation
  const deleteMutation = useMutation({
    mutationFn: hackathonApi.deleteHackathon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hackathons'] })
      message.success('Hackathon deleted successfully!')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete hackathon')
    },
  })

  // Start hackathon mutation
  const startMutation = useMutation({
    mutationFn: hackathonApi.startHackathon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hackathons'] })
      message.success('Hackathon started successfully!')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to start hackathon')
    },
  })

  // End hackathon mutation
  const endMutation = useMutation({
    mutationFn: hackathonApi.endHackathon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hackathons'] })
      message.success('Hackathon ended successfully!')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to end hackathon')
    },
  })

  const hackathons = hackathonsData?.data || []

  const handleAddHackathon = () => {
    setEditingHackathon(null)
    setImageGallery([])
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEditHackathon = (record: Hackathon) => {
    setEditingHackathon(record)
    setImageGallery(record.photo ? [{ id: '1', value: record.photo }] : [])
    form.setFieldsValue({
      title: record.title,
      description: record.description,
      startDate: dayjs(record.startDate),
      endDate: dayjs(record.endDate)
    })
    setIsModalVisible(true)
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const hackathonData = {
        title: values.title,
        description: values.description,
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
        photo: imageGallery.length > 0 ? imageGallery[0].value : undefined
      }

      if (editingHackathon) {
        // Update existing hackathon
        updateMutation.mutate({ id: editingHackathon._id, data: hackathonData })
      } else {
        // Create new hackathon
        createMutation.mutate(hackathonData)
      }
    })
  }

  const handleModalCancel = () => {
    setIsModalVisible(false)
    setImageGallery([])
    form.resetFields()
  }



  const handleDeleteHackathon = (record: Hackathon) => {
    Modal.confirm({
      icon: null,
      title: (
        <div style={{ textAlign: 'center' }}>
          <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <div>Are you sure you want to delete this hackathon?</div>
        </div>
      ),
      content: (
        <div style={{ textAlign: 'center' }}>
          This action cannot be undone for "<b>{record.title}</b>".
        </div>
      ),
      footer: (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }} className='mt-4'>
          <Button
            onClick={() => Modal.destroyAll()}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => {
              deleteMutation.mutate(record._id)
              Modal.destroyAll()
            }}
            loading={deleteMutation.isPending}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Yes, Delete
          </Button>
        </div>
      ),
      centered: true,
    })
  }



  const handleStartHackathon = (record: Hackathon) => {
    Modal.confirm({
      icon: null,
      title: (
        <div style={{ textAlign: 'center' }}>
          <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <div>Are you sure you want to start this hackathon?</div>
        </div>
      ),
      content: (
        <div style={{ textAlign: 'center' }}>
          This will mark "<b>{record.title}</b>" as active and participants can begin working. Registration will be closed.
        </div>
      ),
      footer: (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }} className='mt-4'>
          <Button
            onClick={() => Modal.destroyAll()}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Cancel
          </Button>
          <Button
            type="primary"
            color='green'
            onClick={() => {
              startMutation.mutate(record._id)
              Modal.destroyAll()
            }}
            loading={startMutation.isPending}
            style={{ width: '100%', height: '40px', backgroundColor: '#22c55e', borderColor: '#22c55e' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Yes, Start
          </Button>
        </div>
      ),
      centered: true,
    })
  }

  const handleEndHackathon = (record: Hackathon) => {
    Modal.confirm({
      icon: null,
      title: (
        <div style={{ textAlign: 'center' }}>
          <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
          <div>Are you sure you want to end this hackathon?</div>
        </div>
      ),
      content: (
        <div style={{ textAlign: 'center' }}>
          This will mark "<b>{record.title}</b>" as completed.
        </div>
      ),
      footer: (
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }} className='mt-4'>
          <Button
            onClick={() => Modal.destroyAll()}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => {
              endMutation.mutate(record._id)
              Modal.destroyAll()
            }}
            loading={endMutation.isPending}
            style={{ width: '100%', height: '40px' }}
            className='!rounded-[10px] !h-9 !text-xs'
          >
            Yes, End
          </Button>
        </div>
      ),
      centered: true,
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green'
      case 'upcoming':
        return 'blue'
      case 'completed':
        return 'gray'
      default:
        return 'default'
    }
  }

  const columns = [
    {
      title: 'Image',
      dataIndex: 'photo',
      key: 'photo',
      width: 80,
      render: (photo: string) => (
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
          {photo ? (
            <img 
              src={photo} 
              alt="Hackathon" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextElement) {
                  nextElement.style.display = 'flex';
                }
              }}
            />
          ) : null}
          <div 
            className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
            style={{ display: photo ? 'none' : 'flex' }}
          >
            📚
          </div>
        </div>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string) => (
        <span className="font-medium text-gray-900">{text}</span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => {
        const truncated = text && text.length > 40 ? text.slice(0, 40) + '...' : text;
        return (
          <span className="text-gray-700">{truncated || 'No description'}</span>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (text: string) => (
        <span className="text-gray-700">{dayjs(text).format('DD/MM/YYYY ')}</span>
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (text: string) => (
        <span className="text-gray-700">{dayjs(text).format('DD/MM/YYYY ')}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className="!text-xs !rounded-full !px-2 !py-1">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Registration',
      dataIndex: 'isRegistrationOpen',
      key: 'registration',
      render: (isRegistrationOpen: boolean, record: Hackathon) => {
        // If hackathon is completed, always show "Closed"
        if (record.status === 'completed') {
          return (
            <Tag 
              color="red" 
              className="!text-xs !rounded-full !px-2 !py-1"
            >
              Closed
            </Tag>
          );
        }
        
        // For other statuses, use the isRegistrationOpen value
        return (
          <Tag 
            color={isRegistrationOpen ? 'green' : 'red'} 
            className="!text-xs !rounded-full !px-2 !py-1"
          >
            {isRegistrationOpen ? 'Open' : 'Closed'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Hackathon) => (
        <div className="flex items-center gap-3">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleEditHackathon(record)
            }}
            className="text-gray-500 hover:text-primary p-1"
          />
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleDeleteHackathon(record)
            }}
            className="text-gray-500 hover:text-red-500 p-1"
          />
         
          {record.status === 'upcoming' && (
            <Button
              type="primary"
              className="bg-green-500 hover:!bg-green-600 text-white !rounded-[10px] !h-9 !text-xs"
              onClick={() => handleStartHackathon(record)}
              loading={startMutation.isPending}
              style={{
                backgroundColor: '#22c55e',
                borderColor: '#22c55e',
              }}
            >
              Start Hackathon
            </Button>
          )}
          
          {record.status === 'active' && (
            <Button
              type="default"
              className="bg-red-500 hover:bg-red-600 text-white !rounded-[10px] !h-9 !text-xs"
              onClick={() => handleEndHackathon(record)}
              loading={endMutation.isPending}
              style={{
                backgroundColor: '#ef4444',
                borderColor: '#ef4444',
                color: 'white',
              }}
            >
              End Hackathon
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Hackathons</h1>
          <Button 
            type="primary" 
            onClick={handleAddHackathon}
            className="bg-primary hover:bg-primary/90 h-9 !text-sm !rounded-[10px]"
         
          >
            Add Hackathon
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
        {isLoading ? (
          <div className="p-6">
            <div className="animate-pulse">
              {/* Table Header Skeleton */}
              <div className="flex items-center justify-between mb-4">
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
              
              {/* Table Rows Skeleton */}
              {[...Array(5)].map((_, index) => (
                <div key={index} className="flex items-center justify-between py-4 border-b border-gray-100">
                  <div className="h-4 bg-gray-200 rounded w-48"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  <div className="flex gap-2">
                    <div className="h-8 bg-gray-200 rounded w-8"></div>
                    <div className="h-8 bg-gray-200 rounded w-8"></div>
                    <div className="h-8 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <p className="text-red-600 mb-4">Failed to load hackathons</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={hackathons.map(hackathon => ({ ...hackathon, key: hackathon._id }))}
            pagination={false}
            className="hackathon-table"
            rowClassName="hover:bg-gray-50"
          />
        )}
      </div>

      {/* Edit/Add Modal */}
      <Modal
        title={editingHackathon ? "Edit Hackathon" : "Add Hackathon"}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Save Changes"
        cancelText="Cancel"
        className="hackathon-modal !max-w-[800px] !w-full"
        centered={true}
        okButtonProps={{
          className: "bg-primary hover:bg-primary/90",
          loading: createMutation.isPending || updateMutation.isPending
        }}
      >
        <Form
          form={form}
          layout="vertical"
          className="mt-6"
        >
          <Form.Item
            label={<span className="text-xs font-medium text-gray-700">Title*</span>}
            name="title"
            rules={[{ required: true, message: 'Please enter hackathon title' }]}
            required={false}
          >
            <Input 
              placeholder="Enter hackathon title"
              className="h-10 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
            />
          </Form.Item>


          <div className="flex gap-4 items-start w-full">
            <Form.Item
              label={<span className="text-xs font-medium text-gray-700">Description</span>}
              name="description"
              className="flex-1"
            >
              <Input.TextArea 
                placeholder="Enter hackathon description"
              
                className="rounded-lg border-gray-300 focus:border-primary focus:ring-primary !h-[130px]"
              />
            </Form.Item>

            <Form.Item
              label={<span className="text-xs font-medium text-gray-700">Hackathon Image</span>}
              name="photo"
              className="flex-1"
            >
              <ImageUploader
                actionType={editingHackathon ? "edit" : "add"}
                imageGallery={imageGallery}
                setImageGallery={setImageGallery}
                setIsUploadingGallery={setIsUploadingGallery}
                single={true}
                className=""
              />
            </Form.Item>
          </div>

          <div className="flex gap-4 items-center w-full">
          <Form.Item
            label={<span className="text-xs font-medium text-gray-700">Start Date*</span>}
            name="startDate"
            rules={[{ required: true, message: 'Please select start date' }]}
            required={false}
            className="w-full"
          >
            <DatePicker
              
              placeholder="Select start date "
              format="DD/MM/YYYY "
              className="w-full h-10 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              suffixIcon={<CalendarOutlined className="text-gray-400" />}
            />
          </Form.Item>

          <Form.Item
            label={<span className="text-xs font-medium text-gray-700">End Date*</span>}
            name="endDate"
            rules={[{ required: true, message: 'Please select end date' }]}
            required={false}
            className="w-full"
          >
            <DatePicker
              placeholder="Select end date"
              format="DD/MM/YYYY"
              className="w-full h-10 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              suffixIcon={<CalendarOutlined className="text-gray-400" />}
            />
          </Form.Item>


          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default Hackathon
